/**
 * Utilidades compartidas para filtros dinámicos en cascada (dashboard).
 */
import { getChartDynamicFilters } from './dashboardData';

export function isTimeDynamicFilter(df) {
  if (!df) return false;
  return df.filterKind === 'timeRange' || df.filterKind === 'relativeTime';
}

export function isDataDynamicFilter(df) {
  if (!df?.enabled || !df?.paramName) return false;
  if (isTimeDynamicFilter(df)) return false;
  if (df.filterKind === 'data') return true;
  return df.optionSource === 'query' && Boolean((df.optionsQuery || '').trim());
}

/** Chart builder: spec en memoria permite reconstruir opciones con contexto. */
export function canUseCascadeOptions(chart, df) {
  if (!isDataDynamicFilter(df)) return false;
  const spec = chart?.visualizationSpec;
  return Boolean(spec && Array.isArray(spec.filters) && spec.filters.length > 0);
}

export function sortChartDynamicFilters(filters) {
  return [...(filters || [])].sort((a, b) => {
    const orderA = a?.filterOrder ?? 0;
    const orderB = b?.filterOrder ?? 0;
    if (orderA !== orderB) return orderA - orderB;
    return String(a?.paramName || '').localeCompare(String(b?.paramName || ''));
  });
}

export function optionsContextKey(optionKey, siblingParams = {}) {
  const p =
    siblingParams && typeof siblingParams === 'object' && !Array.isArray(siblingParams)
      ? siblingParams
      : {};
  const keys = Object.keys(p).filter(
    (k) => p[k] !== undefined && p[k] !== null && String(p[k]).trim() !== ''
  );
  if (!keys.length) return optionKey;
  keys.sort();
  return `${optionKey}|${keys.map((k) => `${k}=${String(p[k])}`).join('&')}`;
}

export function buildSiblingParamsForFilter(chart, paramName, { globalFilterValues = {}, globalFilterParamNames = [], getLocalValue }) {
  const params = {};
  const skip = String(paramName || '').trim();
  const isGlobal = (p) => (globalFilterParamNames || []).includes(String(p).trim());

  getChartDynamicFilters(chart).forEach((df) => {
    if (!isDataDynamicFilter(df)) return;
    const p = String(df.paramName || '').trim();
    if (!p || p === skip) return;

    let v;
    if (isGlobal(p)) {
      v = globalFilterValues[p];
    } else if (typeof getLocalValue === 'function') {
      v = getLocalValue(chart, p);
    }
    if (v !== undefined && v !== null && String(v).trim() !== '') {
      params[p] = v;
    }
  });

  return params;
}

export function mapQueryResultToOptions(result, df, keyPrefix) {
  if (!result?.data?.length) return [];
  const row0 = result.data[0];
  const colNames =
    Array.isArray(result.columns) && result.columns.length > 0
      ? result.columns
      : row0 && typeof row0 === 'object'
        ? Object.keys(row0)
        : [];
  const findColKey = (name) => {
    if (!name || !row0) return null;
    if (Object.prototype.hasOwnProperty.call(row0, name)) return name;
    const lower = String(name).toLowerCase();
    return colNames.find((c) => String(c).toLowerCase() === lower) || colNames[0] || null;
  };
  const labelCol = findColKey(df.labelColumn) || findColKey(df.paramName);
  const valueCol = findColKey(df.valueColumn) || findColKey(df.paramName);
  const trimIfString = (v) => (v != null && typeof v === 'string' ? v.trim() : v);

  return result.data.map((row, i) => {
    const label = labelCol ? String(row[labelCol] ?? '').trim() : '';
    const value = valueCol ? trimIfString(row[valueCol]) : '';
    return {
      label: label || value,
      value: value || label,
      _key: `${keyPrefix}-${i}`,
    };
  });
}

export function pickValidFilterValue(currentVal, savedVal, options, { allowEmpty = false } = {}) {
  const list = Array.isArray(options) ? options : [];
  const candidates = [currentVal, savedVal];
  for (const c of candidates) {
    if (c === undefined || c === null) continue;
    if (allowEmpty && String(c).trim() === '') return '';
    if (list.some((o) => String(o.value) === String(c))) return c;
  }
  if (allowEmpty) return '';
  return list.find((o) => String(o.value ?? '').trim() !== '')?.value ?? list[0]?.value;
}

/** Opción «sin filtro» cuando allowExecuteWithoutSelection está activo. */
export const FILTER_ANY_OPTION = { value: '', label: 'Cualquiera', _key: '__filter_any__' };

export function withOptionalAnyOption(options, allowWithoutSelection) {
  const list = Array.isArray(options) ? options.filter((o) => String(o?.value ?? '') !== '') : [];
  if (!allowWithoutSelection) return list;
  return [{ ...FILTER_ANY_OPTION }, ...list];
}
